import { translate } from '@thoughtspot/i18n';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import {
    isEnhancedLiveboardInteractivityWithCSREnabled,
    isLiveboardPermissionV2Enabled,
} from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { ReactElement } from 'react';
import {
    AccessMode,
    AnswerColumnEffectiveType,
    ColumnTimeBucket,
    FalconDataType,
    FilterColumn,
    FilterContentInput,
    FilterContentValue,
    FilterGroup,
    FilterGroupId,
    ObjectPermission,
    Parameter,
    PinboardPermissions,
    ShareMode,
} from '/@services/generated/graphql-types';
import { LinkFilterRow } from './add-filter/pinboard-filter.util';
import { DateFilterProto } from './date-picker/date-filter';
import {
    DateFilter,
    NUMERICAL_DATE_BUCKETS,
} from './date-range-filter/date-range-filter.util';

export enum FILTER_TYPE {
    MEASURE_FILTER,
    ATTRIBUTE_FILTER,
    DATE_RANGE_FILTER,
}

export interface FilterContainerProps {
    filterGroup: FilterGroup;
    onFilterClose: () => void;
    isReadOnly?: boolean;
    filterDetails?: FilterDetailsData;
    onFilterChange?: (data: FilterDetailsData) => void;
    showErrorState?: boolean;
    setShowErrorState?: (isShowError: boolean) => void;
    isSingleValueSelect?: boolean;
    singleSelectValue?: FilterDetailsData;
    setSingleSelectValue?: (data: FilterDetailsData) => void;
    isBulkFilterMode?: boolean;
    setIsBulkFilterMode?: (value: boolean) => void;
    worksheetNames?: string[];
    onRequestAccessClickConfirm?: () => void;
    isHiddenColumnFilter?: boolean;
    isPinboardCentralisedFilterModal?: boolean;
    showLoadingIndicator?: boolean;
    onError?: (errorText: string) => void;
}

export interface FilterDetailsData
    extends AttributeFilter,
        MeasureFilter,
        DateFilter,
        DateFilterProto {}

export interface FilterErrorConfig {
    isVisible: boolean;
    isLinkVisible?: boolean;
    message: string;
    type: BannerAlertType | null;
}

export const FALCON_DATE_DATA_TYPES = [
    FalconDataType.Date,
    FalconDataType.DateTime,
    FalconDataType.Time,
];

export const groupIdType = {
    answerColumnId: 'answerColumnId',
    logicalColumnId: 'logicalColumnId',
};

export enum MEASURE_FILTER_TYPE {
    GREATER_THAN = 'FILTER_GREATER_THAN',
    GREATER_OR_EQUAL = 'FILTER_GREATER_OR_EQUAL',
    LESS_THAN = 'FILTER_LESS_THAN',
    LESS_OR_EQUAL = 'FILTER_LESS_OR_EQUAL',
    IS_BETWEEN = 'FILTER_IS_BETWEEN',
    IS_IN = 'FILTER_IS_IN',
    IS_NOT_IN = 'FILTER_IS_NOT_IN',
}

export interface MeasureFilterCondition {
    key: string; // unique key in order to create list of react components
    filterType: MEASURE_FILTER_TYPE;
    value: string[];
    filterId?: string;
}

export interface MeasureFilter {
    addUpdateConditions?: MeasureFilterCondition[];
    removedConditions?: MeasureFilterCondition[];
}

export interface AttributeFilter {
    includeValues?: string[];
    excludeValues?: string[];
}

export const hasAtleastOneMaskedFilter = (filterGroups: FilterGroup[]) => {
    return filterGroups?.some(filter => filter.columnInfo?.isMasked);
};

export const isPermissionV2Enabled = () => {
    return (
        isLiveboardPermissionV2Enabled() ||
        isEnhancedLiveboardInteractivityWithCSREnabled()
    );
};

export const checkForMaskedFilterChip = (filterGroups: FilterGroup[]) => {
    if (!isEnhancedLiveboardInteractivityWithCSREnabled()) {
        return false;
    }
    return hasAtleastOneMaskedFilter(filterGroups);
};

/**
 * Returns whether the filer applied on the column should be readonly
 * @param permission
 * @param filterGroup
 */
export function isReadOnlyFilter(
    permission: PinboardPermissions,
    filterGroup: FilterGroup,
): boolean {
    return (
        permission?.dataSourcePermissions?.find(
            dsPermission =>
                dsPermission.id === filterGroup?.groupId?.dataSourceId,
        )?.permission === ShareMode.NoAccess ||
        permission?.columnPermissions?.find(
            columnPermission =>
                columnPermission.id === filterGroup?.groupId?.logicalColumnId,
        )?.permission === ShareMode.NoAccess
    );
}

export function isHiddenColumnFilter(
    permission: PinboardPermissions,
    filterGroup: FilterGroup,
): boolean {
    return (
        _.find(
            permission?.columnPermissions,
            columnPermission =>
                columnPermission.id === filterGroup?.groupId?.logicalColumnId,
        )?.isHidden ?? false
    );
}

export const hasReadOnlyLinkedFilter = (
    permission: PinboardPermissions,
    linkedFilters?: LinkFilterRow[],
): boolean =>
    linkedFilters?.some(
        linkedFilter =>
            permission?.dataSourcePermissions?.find(
                ds => ds.id === linkedFilter.dataSourceId,
            )?.permission === ShareMode.NoAccess,
    );

export function hasPermissionToAtleastOneWorksheet(
    permission: PinboardPermissions,
): boolean {
    return permission?.dataSourcePermissions?.some(
        dsPermission => dsPermission.permission !== ShareMode.NoAccess,
    );
}

export function hasAccessToAllWorksheets(
    permissions: PinboardPermissions,
): boolean {
    return !permissions?.dataSourcePermissions?.some(
        ({ permission }) => permission === ShareMode.NoAccess,
    );
}

export const hasPermissionToAtleastOneParameter = (
    permission: PinboardPermissions,
    parameters: Parameter[],
): boolean =>
    parameters?.some(
        parameter =>
            permission?.dataSourcePermissions?.find(
                ds => ds.id === parameter?.owner?.id,
            )?.permission !== ShareMode.NoAccess,
    );

export function getAccessibleWorksheetsForParameter(
    parameters: Parameter[],
    permission: PinboardPermissions,
): Parameter[] {
    return parameters?.filter(
        parameter =>
            permission?.dataSourcePermissions?.find(
                ds => ds.id === parameter?.owner?.id,
            )?.permission !== ShareMode.NoAccess,
    );
}

/**
 * Returns whether the filer applied on the column should be readonly
 * @param permission
 */
export function isReadOnly(permission: ObjectPermission): boolean {
    return permission?.dataSourceAccessLevel === AccessMode.NoAccess;
}
/**
 * Returns whether the filer applied on the column should be readonly
 * @param permission
 * @param parameter ameterameter
 */
export const isReadOnlyParameter = (
    permission: PinboardPermissions,
    parameter: Parameter,
): boolean =>
    permission?.dataSourcePermissions?.find(
        dsPermission => dsPermission.id === parameter?.owner?.id,
    )?.permission === ShareMode.NoAccess;

export function isFilterReadOnly(
    permission: PinboardPermissions,
    filterGroup: FilterGroup,
    isFilterPermissionEnabled: boolean,
): boolean {
    if (isFilterPermissionEnabled) {
        return isReadOnlyFilter(permission, filterGroup);
    }
    return isReadOnly(permission?.pinboardLevelPermission);
}

export function getFilterType(
    columnDataType: FalconDataType,
    filterColumnType: AnswerColumnEffectiveType,
    filterTimeBucket: ColumnTimeBucket,
): FILTER_TYPE {
    if (filterColumnType === AnswerColumnEffectiveType.Measure) {
        return FILTER_TYPE.MEASURE_FILTER;
    }
    if (filterColumnType === AnswerColumnEffectiveType.Attribute) {
        if (NUMERICAL_DATE_BUCKETS.includes(filterTimeBucket)) {
            return FILTER_TYPE.ATTRIBUTE_FILTER;
        }
        if (FALCON_DATE_DATA_TYPES.includes(columnDataType)) {
            // If column has numerical time bucket, then also
            // return Date_RANGE_FILTER because in that case the
            // columnDataType can be numeric such as Int32 or Int64
            return FILTER_TYPE.DATE_RANGE_FILTER;
        }
        return FILTER_TYPE.ATTRIBUTE_FILTER;
    }
    return null;
}

// For Date Range Filter, we always send logicalColumnId as param to the API call.
export function getGroupId(
    columnGroupId: FilterGroupId,
    isDateRangeFilter: boolean,
    filterColumn: FilterColumn,
): FilterGroupId {
    // you can not delete a property from readonly object(columnGroupId) so must take a clone
    const filterGroupId: FilterGroupId = _.clone(columnGroupId);
    if (
        groupIdType.answerColumnId in columnGroupId &&
        groupIdType.logicalColumnId in columnGroupId &&
        !!filterGroupId.answerColumnId &&
        !!filterGroupId.logicalColumnId
    ) {
        // if we have formulaId present then for date-range filter also we have to pass answerColumnId instead logicalColumnId
        if (isDateRangeFilter && !filterColumn?.formulaId)
            delete filterGroupId.answerColumnId;
        else delete filterGroupId.logicalColumnId;
    }
    return filterGroupId;
}

// Comparator function for sorting array of strings which have both
// alphabetic and numeric strings
// Eg: [1, 3, ball, 10, apple, 11, 2] should be sorted as
// [1, 2, 3, 9, 10, 11, apple, ball]
export function alphaNumericCompareFn(a: string, b: string) {
    return a.localeCompare(b, 'en', { numeric: true });
}
export const isFilterNonEditable = (filterGroup: FilterGroup) => {
    return filterGroup.isEditable === false;
};

export const applyAnswerFilter = (
    addUpdateRemoveFilter: (
        addUpdateFilterContent: FilterContentInput[],
        filterIdsToRemove: string[],
    ) => void,
    addUpdateFilter: (filterContent: FilterContentInput[]) => void,
    removeFilter: (filterIds: string[]) => void,
    filterContent: FilterContentInput[] = [],
    removeFilterIds: string[] = [],
) => {
    if (
        !_.isEmpty(filterContent) &&
        !_.isEmpty(removeFilterIds) &&
        !_.isEmpty(filterContent[0]?.value)
    ) {
        addUpdateRemoveFilter(filterContent, removeFilterIds);
    } else if (
        !_.isEmpty(filterContent) &&
        !_.isEmpty(filterContent[0]?.value) // we should not add empty filter in view mode
    ) {
        addUpdateFilter(filterContent);
    } else if (!_.isEmpty(removeFilterIds)) {
        removeFilter(removeFilterIds);
    }
};

export const applyPinboardFilter = (
    addUpdateRemoveFilter: (
        addUpdateFilterContent: FilterContentInput[],
        filterIdsToRemove: string[],
    ) => void,
    addUpdateFilter: (filterContent: FilterContentInput[]) => void,
    removeFilter: (filterIds: string[]) => void,
    filterContent: FilterContentInput[] = [],
    removeFilterIds: string[] = [],
) => {
    if (!_.isEmpty(filterContent) && !_.isEmpty(removeFilterIds)) {
        addUpdateRemoveFilter(filterContent, removeFilterIds);
    } else if (
        !_.isEmpty(filterContent) &&
        !_.isEmpty(filterContent[0]?.value) // we should not add empty filter in view mode
    ) {
        addUpdateFilter(filterContent);
    } else if (!_.isEmpty(removeFilterIds)) {
        removeFilter(removeFilterIds);
    }
};

export const getAttributeFilterValue = (value: FilterContentValue) => {
    if (_.isNil(value?.key)) {
        return translate('NULL_VALUE_PLACEHOLDER_LABEL', {
            skipInterpolation: true,
        });
    }
    if (_.isEmpty(value.key)) {
        return translate('EMPTY_VALUE_PLACEHOLDER_LABEL', {
            skipInterpolation: true,
        });
    }
    return _.toLower(value.key);
};

export const ariaLiveRegion = ({
    ariaLabel,
}: {
    ariaLabel: string;
}): ReactElement => {
    return React.createElement('span', {
        role: 'region',
        'aria-live': 'polite',
        'aria-label': ariaLabel,
    });
};

export const getAlertConfigForError = (message: string): FilterErrorConfig => ({
    isVisible: true,
    isLinkVisible: false,
    type: BannerAlertType.ERROR,
    message,
});

export const getAlertConfigForWarning = ({
    isLinkVisible = true,
    message,
}: {
    isLinkVisible: boolean;
    message: string;
}): FilterErrorConfig => ({
    isVisible: true,
    isLinkVisible,
    type: BannerAlertType.WARNING,
    message,
});

export const getDefaultAlertConfig = (): FilterErrorConfig => ({
    isVisible: false,
    isLinkVisible: false,
    type: null,
    message: null,
});

/**
 * Returns names of inaccessible sources
 * @param permission
 */
export function getDataSourceNames(
    permission: PinboardPermissions,
    filterGroup: FilterGroup,
): string[] {
    return permission?.dataSourcePermissions
        ?.filter(ds => ds.id === filterGroup?.groupId?.dataSourceId)
        ?.map(ds => ds.name);
}

export const getWarningBannerMessage = (worksheetNames: string[]): string => {
    return isPermissionV2Enabled()
        ? translate('permissionV2RequestAccessBanner.copy', {
              worksheetNames: worksheetNames.join(', '),
          })
        : translate('requestAccessBanner.copy', {
              worksheetNames: worksheetNames.join(', '),
          });
};
